// Action-mapped input: keyboard + mouse + gamepad feed the same actions
// (ARCHITECTURE "Input map"). Edge detection, buffering and the post-pause
// resync live in the pure ActionStates (./actions.ts); this file only reads
// devices.
//
// Physical keyboard state is tracked with window listeners rather than the
// scene's KeyboardPlugin: the plugin stops processing events while the Game
// scene is paused, so a key released during an overlay would read as stuck.
// The scene keyboard is still used to capture Space/Tab/arrows (no page scroll
// or focus change).

import Phaser from 'phaser';
import { audio } from '../../audio';
import type { Action, InputApi, Vec } from '../../core/contracts';
import { save } from '../../core/save';
import { ActionStates } from './actions';

const ACTIONS: readonly Action[] = [
  'left',
  'right',
  'up',
  'down',
  'jump',
  'fire',
  'signature',
  'dash',
  'ultimate',
  'interact',
  'salvage',
  'weaponNext',
  'weaponPrev',
  'weapon1',
  'weapon2',
  'pause',
  'map',
];

/** Two fresh presses of the same direction within this real time (s) trigger a dash. */
const DOUBLE_TAP_WINDOW = 0.3;

/** Directions keep reading as held across a resync (holding D through a pause keeps running). */
const LEVEL_ACTIONS: readonly Action[] = ['left', 'right', 'up', 'down'];

/** KeyboardEvent.code → actions. Codes are layout-independent (WASD stays WASD on AZERTY positions). */
const KEY_MAP: Readonly<Record<string, readonly Action[]>> = {
  KeyA: ['left'],
  ArrowLeft: ['left'],
  KeyD: ['right'],
  ArrowRight: ['right'],
  KeyW: ['up'],
  ArrowUp: ['up'],
  KeyS: ['down'],
  ArrowDown: ['down'],
  Space: ['jump'],
  ShiftLeft: ['dash'],
  ShiftRight: ['dash'],
  KeyR: ['ultimate'],
  KeyE: ['interact'],
  KeyX: ['salvage'],
  Digit1: ['weapon1'],
  Numpad1: ['weapon1'],
  Digit2: ['weapon2'],
  Numpad2: ['weapon2'],
  Escape: ['pause'],
  Tab: ['map'],
};

/** Keys whose browser default (scroll, focus change) must never happen during play. */
const CAPTURE_KEYS = ['SPACE', 'TAB', 'UP', 'DOWN', 'LEFT', 'RIGHT'];

/** Standard-mapping gamepad button indices. */
const PAD = {
  A: 0,
  B: 1,
  X: 2,
  Y: 3,
  LB: 4,
  RB: 5,
  LT: 6,
  RT: 7,
  BACK: 8,
  START: 9,
  DUP: 12,
  DDOWN: 13,
  DLEFT: 14,
  DRIGHT: 15,
} as const;

/** Analog triggers count as pressed above this value. */
const TRIGGER_THRESHOLD = 0.4;
/** Stick deflection that counts as a digital direction (left/right/up/down actions). */
const STICK_DIGITAL = 0.5;
/** Past this deflection the move axis reads as full speed (sticks rarely reach 1.0 on diagonals). */
const STICK_FULL = 0.9;
/** Gamepad aim distance from the origin (px). */
const PAD_AIM_DISTANCE = 420;
/** Wheel: minimum gap between two weapon-switch notches (trackpads emit bursts). */
const WHEEL_COOLDOWN = 0.12;
/** Mouse movement (screen px) that switches the active device back to mouse. */
const MOUSE_SWITCH_DIST = 3;

/** Deadzone with rescale so motion starts at 0 right outside the deadzone. */
function applyDeadzone(v: number, dz: number): number {
  const a = Math.abs(v);
  if (a <= dz) return 0;
  const k = Math.min(1, (a - dz) / Math.max(1e-3, 1 - dz));
  return Math.sign(v) * (k >= STICK_FULL ? 1 : k);
}

class Input implements InputApi {
  private readonly scene: Phaser.Scene;
  private readonly states = new ActionStates<Action>(ACTIONS, LEVEL_ACTIONS);
  private readonly idx: Record<Action, number>;

  private readonly physicalKeys = new Set<string>();
  private readonly cleanups: (() => void)[] = [];

  private _enabled = true;
  private _moveX = 0;
  /** Double-tap dash: last fresh direction press (±1, 0 = none) and when it happened. */
  private lastTap = 0;
  /** Real-time seconds of the last fresh direction press. */
  private lastTapAt = -1;
  private _moveY = 0;
  private _usingGamepad = false;
  private resyncPending = false;
  private audioUnlocked = false;
  private destroyed = false;

  private wheelCooldown = 0;
  private wheelAccum = 0;
  private lastMouseX = Number.NaN;
  private lastMouseY = Number.NaN;

  private readonly pointer: Vec = { x: 0, y: 0 };
  private readonly mouseWorld = new Phaser.Math.Vector2();
  /** Last non-idle right-stick direction (unit vector). */
  private padAimX = 1;
  private padAimY = 0;
  private padAimValid = false;
  /** Pad buttons held last update (device switching on a new press). */
  private readonly padPrev: boolean[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.idx = {} as Record<Action, number>;
    for (const a of ACTIONS) this.idx[a] = this.states.indexOf(a);
    this.installKeyboard();
    this.installMouse();
    this.installSceneEvents();
  }

  // ── InputApi ──

  get enabled(): boolean {
    return this._enabled;
  }

  set enabled(on: boolean) {
    if (on === this._enabled) return;
    this._enabled = on;
    // Coming back from a menu: whatever is still held from it must not fire.
    if (on) this.resyncPending = true;
    else this.states.clear();
  }

  get moveX(): number {
    return this._enabled ? this._moveX : 0;
  }

  get moveY(): number {
    return this._enabled ? this._moveY : 0;
  }

  get usingGamepad(): boolean {
    return this._usingGamepad;
  }

  get pointerWorld(): Vec {
    return this.pointer;
  }

  held(a: Action): boolean {
    return this.states.held(a);
  }

  pressed(a: Action): boolean {
    return this.states.pressed(a);
  }

  released(a: Action): boolean {
    return this.states.released(a);
  }

  sincePressed(a: Action): number {
    return this.states.sincePressed(a);
  }

  consume(a: Action): void {
    this.states.consume(a);
  }

  aim(originX: number, originY: number): { x: number; y: number; angle: number } {
    if (this._usingGamepad) {
      if (!this.padAimValid) {
        // Never aimed with the stick yet: keep pointing toward the last known reticle side.
        const dx = this.pointer.x - originX;
        const dy = this.pointer.y - originY;
        const d = Math.hypot(dx, dy);
        if (d > 1) {
          this.padAimX = dx / d;
          this.padAimY = dy / d;
        }
      }
      this.pointer.x = originX + this.padAimX * PAD_AIM_DISTANCE;
      this.pointer.y = originY + this.padAimY * PAD_AIM_DISTANCE;
      return { x: this.pointer.x, y: this.pointer.y, angle: Math.atan2(this.padAimY, this.padAimX) };
    }
    const x = this.pointer.x;
    const y = this.pointer.y;
    return { x, y, angle: Math.atan2(y - originY, x - originX) };
  }

  update(dt: number): void {
    if (this.destroyed) return;
    this.wheelCooldown = Math.max(0, this.wheelCooldown - dt);
    const raw = this.states.raw;
    raw.fill(false);

    // Keyboard.
    for (const code of this.physicalKeys) {
      const acts = KEY_MAP[code];
      if (acts) for (const a of acts) raw[this.idx[a]] = true;
    }
    const kx = (raw[this.idx.right] ? 1 : 0) - (raw[this.idx.left] ? 1 : 0);
    const ky = (raw[this.idx.down] ? 1 : 0) - (raw[this.idx.up] ? 1 : 0);

    // Mouse buttons (the shared pointer keeps updating while this scene is paused).
    const ptr = this.scene.input?.activePointer;
    if (ptr) {
      if (ptr.leftButtonDown()) raw[this.idx.fire] = true;
      if (ptr.rightButtonDown()) raw[this.idx.signature] = true;
      this.updateMouseWorld(ptr);
    }

    // Gamepad.
    const pad = this.activePad();
    let px = 0;
    let py = 0;
    if (pad) {
      const dz = Phaser.Math.Clamp(save.settings.deadzone, 0, 0.5);
      const b = (i: number) => {
        const btn = pad.buttons[i];
        return !!btn && (btn.pressed || btn.value > TRIGGER_THRESHOLD);
      };
      const ax = (i: number) => {
        const axis = pad.axes[i];
        return axis ? axis.value : 0;
      };
      let anyPad = false;
      for (let i = 0; i < pad.buttons.length; i++) {
        const now = b(i);
        if (now && !this.padPrev[i]) anyPad = true;
        this.padPrev[i] = now;
      }
      px = applyDeadzone(ax(0), dz);
      py = applyDeadzone(ax(1), dz);
      if (b(PAD.DLEFT)) px = -1;
      if (b(PAD.DRIGHT)) px = 1;
      if (b(PAD.DUP)) py = -1;
      const rx = ax(2);
      const ry = ax(3);
      const rmag = Math.hypot(rx, ry);
      if (rmag > Math.max(0.25, dz)) {
        this.padAimX = rx / rmag;
        this.padAimY = ry / rmag;
        this.padAimValid = true;
        anyPad = true;
      }
      if (px !== 0 || py !== 0) anyPad = true;

      if (px <= -STICK_DIGITAL) raw[this.idx.left] = true;
      if (px >= STICK_DIGITAL) raw[this.idx.right] = true;
      if (py <= -STICK_DIGITAL) raw[this.idx.up] = true;
      if (py >= STICK_DIGITAL) raw[this.idx.down] = true;
      if (b(PAD.A)) raw[this.idx.jump] = true;
      if (b(PAD.RT)) raw[this.idx.fire] = true;
      if (b(PAD.LT)) raw[this.idx.signature] = true;
      if (b(PAD.B) || b(PAD.RB)) raw[this.idx.dash] = true;
      if (b(PAD.Y)) raw[this.idx.ultimate] = true;
      if (b(PAD.X)) raw[this.idx.interact] = true;
      if (b(PAD.LB)) raw[this.idx.weaponNext] = true;
      if (b(PAD.START)) raw[this.idx.pause] = true;
      if (b(PAD.BACK)) raw[this.idx.map] = true;
      if (b(PAD.DDOWN)) raw[this.idx.salvage] = true;

      if (anyPad) {
        this._usingGamepad = true;
        this.onFirstInput();
      }
    }

    // Axes: digital keyboard wins over the stick when both are used.
    this._moveX = kx !== 0 ? kx : px;
    this._moveY = ky !== 0 ? ky : py;

    if (this.resyncPending) {
      this.resyncPending = false;
      this.states.resync();
      return;
    }
    this.states.step(dt, this._enabled);
    this.detectDoubleTap();
  }

  /**
   * Double-tapping a direction (→ → or ← ←) dashes: the second fresh press of the same
   * direction within DOUBLE_TAP_WINDOW (real time) latches a dash press (reported on the next update;
   * the dash direction comes from the held direction).
   */
  private detectDoubleTap(): void {
    if (!this._enabled) {
      this.lastTap = 0;
      return;
    }
    const dir = this.states.pressed('right') ? 1 : this.states.pressed('left') ? -1 : 0;
    if (dir === 0) return;
    // Real time, not game time: tap rhythm is a human timing and must not stretch when frames drop.
    const now = performance.now() / 1000;
    if (dir === this.lastTap && now - this.lastTapAt <= DOUBLE_TAP_WINDOW) {
      this.states.latch('dash');
      this.lastTap = 0;
      return;
    }
    this.lastTap = dir;
    this.lastTapAt = now;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const off of this.cleanups) {
      try {
        off();
      } catch {
        // Scene systems may already be torn down.
      }
    }
    this.cleanups.length = 0;
    this.physicalKeys.clear();
  }

  // ── Devices ──

  private installKeyboard(): void {
    const kb = this.scene.input?.keyboard;
    if (kb) {
      try {
        kb.addCapture(CAPTURE_KEYS);
      } catch {
        // Capture is a nicety (no page scroll); never fatal.
      }
    }
    const down = (ev: KeyboardEvent) => {
      const acts = KEY_MAP[ev.code];
      this._usingGamepad = false;
      this.onFirstInput();
      if (!acts) return;
      this.physicalKeys.add(ev.code);
      if (ev.repeat) return;
      for (const a of acts) this.states.latch(a);
    };
    const up = (ev: KeyboardEvent) => {
      this.physicalKeys.delete(ev.code);
    };
    // Alt-tab / focus loss: the browser never delivers the keyups.
    const blur = () => this.physicalKeys.clear();
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    this.cleanups.push(() => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    });
  }

  private installMouse(): void {
    const input = this.scene.input;
    if (!input) return;
    const onDown = (p: Phaser.Input.Pointer) => {
      this._usingGamepad = false;
      this.onFirstInput();
      if (p.button === 0) this.states.latch('fire');
      else if (p.button === 2) this.states.latch('signature');
    };
    const onWheel = (_p: Phaser.Input.Pointer, _objs: unknown, _dx: number, dy: number) => {
      this._usingGamepad = false;
      if (dy === 0) return;
      // Reset the accumulator when the direction flips so a reversal reacts immediately.
      if (Math.sign(dy) !== Math.sign(this.wheelAccum)) this.wheelAccum = 0;
      this.wheelAccum += dy;
      if (this.wheelCooldown > 0 || Math.abs(this.wheelAccum) < 1) return;
      this.states.latch(this.wheelAccum > 0 ? 'weaponNext' : 'weaponPrev');
      this.wheelAccum = 0;
      this.wheelCooldown = WHEEL_COOLDOWN;
    };
    input.on(Phaser.Input.Events.POINTER_DOWN, onDown);
    input.on(Phaser.Input.Events.POINTER_WHEEL, onWheel);
    this.cleanups.push(() => {
      input.off(Phaser.Input.Events.POINTER_DOWN, onDown);
      input.off(Phaser.Input.Events.POINTER_WHEEL, onWheel);
    });
  }

  private installSceneEvents(): void {
    const events = this.scene.events;
    // Presses made while the scene was paused (overlays) must never fire on the first frame back.
    const resync = () => {
      this.resyncPending = true;
    };
    events.on(Phaser.Scenes.Events.RESUME, resync);
    events.on(Phaser.Scenes.Events.WAKE, resync);
    events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
    events.once(Phaser.Scenes.Events.DESTROY, () => this.destroy());
    this.cleanups.push(() => {
      events.off(Phaser.Scenes.Events.RESUME, resync);
      events.off(Phaser.Scenes.Events.WAKE, resync);
    });
    // The scene starts with whatever is already held (e.g. Space from the hangar) suppressed.
    this.resyncPending = true;
  }

  private updateMouseWorld(ptr: Phaser.Input.Pointer): void {
    const moved =
      !Number.isFinite(this.lastMouseX) ||
      Math.abs(ptr.x - this.lastMouseX) + Math.abs(ptr.y - this.lastMouseY) > MOUSE_SWITCH_DIST;
    if (moved) {
      if (Number.isFinite(this.lastMouseX) && this._usingGamepad) this._usingGamepad = false;
      this.lastMouseX = ptr.x;
      this.lastMouseY = ptr.y;
    }
    if (this._usingGamepad) return;
    const cam = this.scene.cameras?.main;
    if (!cam) return;
    // Recomputed every frame: the camera moves even when the mouse does not.
    ptr.positionToCamera(cam, this.mouseWorld);
    this.pointer.x = this.mouseWorld.x;
    this.pointer.y = this.mouseWorld.y;
  }

  private activePad(): Phaser.Input.Gamepad.Gamepad | null {
    const gp = this.scene.input?.gamepad;
    if (!gp || !gp.enabled) return null;
    if (gp.pad1 && gp.pad1.connected) return gp.pad1;
    for (const p of gp.gamepads) if (p && p.connected) return p;
    return null;
  }

  private onFirstInput(): void {
    if (this.audioUnlocked) return;
    this.audioUnlocked = true;
    try {
      audio.unlock();
    } catch {
      // Audio module not ready yet: the next scene's input will try again.
      this.audioUnlocked = false;
    }
  }
}

export function createInput(scene: Phaser.Scene): InputApi {
  return new Input(scene);
}
